/**
 * Direct OpenAI call helper for the Demo flow.
 *
 * IMPORTANT:
 * - This is a client-side call. Do NOT use in production with a real key.
 * - Provide your key via env var: REACT_APP_OPENAI_API_KEY
 * - Optional: REACT_APP_OPENAI_MODEL (defaults to gpt-4o-mini)
 */

const DEFAULT_BASE_URL = "https://api.openai.com/v1";
const DEFAULT_MODEL = "gpt-4.1-mini";

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Calls OpenAI Chat Completions API and returns assistant text.
 * @param {Object} opts
 * @param {string} opts.prompt
 * @param {AbortSignal} [opts.signal]
 * @param {string} [opts.apiKey]
 * @param {string} [opts.model]
 * @param {string} [opts.baseUrl]
 * @returns {Promise<string>}
 */
export async function getChatGptSummaryDirect({
  prompt,
  signal,
  apiKey = "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",
  model = process.env.REACT_APP_OPENAI_MODEL || DEFAULT_MODEL,
  baseUrl = DEFAULT_BASE_URL,
}) {
  if (!prompt || typeof prompt !== "string") {
    throw new Error("OpenAI direct call requires a non-empty prompt string.");
  }
  if (!apiKey) {
    throw new Error(
      "Missing OpenAI API key. Set REACT_APP_OPENAI_API_KEY in your environment."
    );
  }

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model,
      messages: [
        {
          role: "user",
          content: prompt,
        },
      ],
      temperature: 0.4,
    }),
    signal,
  });

  const text = await res.text();
  const json = safeJsonParse(text);

  if (!res.ok) {
    const message =
      json?.error?.message ||
      (typeof text === "string" && text.trim()
        ? text.trim()
        : `OpenAI request failed with status ${res.status}`);
    throw new Error(message);
  }

  const content = json?.choices?.[0]?.message?.content;
  if (!content || typeof content !== "string") {
    throw new Error("OpenAI response did not include message content.");
  }

  return content;
}

